import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { passCsrfToken } from '../../helpers';
import { getPackages, deletePackage, updatePackage } from '../../api/packageApi';
import { getAllPartners } from '../../api/partnerApi';
import swal from 'sweetalert';
import AdminListingTable from './AdminListingTable';
import { normalizeList } from '../../helpers/adminListingTableNormalizers';

export default function PrivateToursList() {
  const history = useHistory();
  const location = useLocation();
  const filtersRef = useRef({ search: '', status: '', published: '', vendor_id: '' });
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    published: '',
    vendor_id: ''
  });

  filtersRef.current = filters;

  const fetchTours = useCallback((params = {}) => {
    setFetchError(false);
    setLoading(true);
    const f = params.filtersOverride != null ? params.filtersOverride : filtersRef.current;
    const page = params.page || new URLSearchParams(location.search).get('page') || 1;
    const q = {
      admin_list: 'true',
      per_page: 20,
      page,
      ...(f.search ? { 'q[name_cont]': f.search } : {}),
      ...(f.status ? { status: f.status } : {}),
      ...(f.published ? { published: f.published } : {}),
      ...(f.vendor_id ? { vendor_id: f.vendor_id } : {}),
      ...params
    };
    delete q.filtersOverride;
    getPackages(q)
      .then((res) => {
        const raw = res.data.packages || res.data || [];
        const list = Array.isArray(raw) ? raw : [];
        setRows(normalizeList(list, 'private'));
        setMeta(res.data.meta || {});
        setLoading(false);
        setFetchError(false);
      })
      .catch((e) => {
        console.warn('Fetch Private Tours Error', e);
        setLoading(false);
        setFetchError(true);
      });
  }, [location.search]);

  useEffect(() => {
    passCsrfToken(document, axios);
  }, []);

  useEffect(() => {
    getAllPartners({ per_page: 500 })
      .then((r) => setVendors(r.data?.vendors || r.data || []))
      .catch(() => setVendors([]));
  }, []);

  useEffect(() => {
    fetchTours({});
  }, [fetchTours]);

  const handleFilterChange = (newFilters) => {
    const merged = { ...filters, ...newFilters };
    setFilters((prev) => ({ ...prev, ...newFilters }));
    history.replace({ pathname: location.pathname, search: '?page=1' });
    fetchTours({ filtersOverride: merged, page: 1 });
  };

  const handlePageChange = (page) => {
    history.push({ pathname: location.pathname, search: `?page=${page}` });
  };

  const handleDelete = (row) => {
    const id = row.id || row.raw?.idx;
    if (!id) return;
    swal({ title: 'Are you sure?', text: 'This private tour will be deleted.', icon: 'warning', buttons: true, dangerMode: true })
      .then((ok) => {
        if (!ok) return;
        setDeletingId(id);
        deletePackage(id)
          .then(() => {
            swal('Deleted', 'Private tour removed. Refreshing list.', { icon: 'success' });
            setRows((prev) => prev.filter((r) => r.id !== id));
            fetchTours({});
          })
          .catch((e) => {
            const msg = (e.response?.data?.errors || [])?.join(' ') || e.response?.data?.message || e.message || 'Delete failed';
            swal('Delete failed', msg, 'error');
          })
          .finally(() => setDeletingId(null));
      });
  };

  const handlePublishedToggle = (row) => {
    const id = row.id || row.raw?.idx;
    if (!id) return;
    const next = !row.published;
    updatePackage(id, { published: next })
      .then(() => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, published: next } : r)));
      })
      .catch((e) => {
        const msg = (e.response?.data?.errors || [])?.join(' ') || e.message || 'Update failed';
        swal('Update failed', msg, 'error');
      });
  };

  return (
    <div className="ui container">
      <AdminListingTable
        type="private"
        title="Private Tours"
        rows={rows}
        meta={meta}
        loading={loading}
        fetchError={fetchError}
        onRetry={() => fetchTours({})}
        filters={{ ...filters, vendorOptions: vendors }}
        onFilterChange={handleFilterChange}
        onPageChange={handlePageChange}
        onPublishedToggle={handlePublishedToggle}
        onDelete={handleDelete}
        deleteDisabled={deletingId != null}
        viewHref={(row) => `/admin/private_tour_details/${row.id}`}
        editHref={(row) => `/admin/private_tour_form/${row.id}`}
        addHref="/admin/private_tour_form"
        backHref="/admin"
      />
    </div>
  );
}
